'use client'

import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import React, { useState, useRef, useMemo, memo, useEffect } from 'react'

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger
} from '@/_components/ui/accordion'
import { getIcon } from '@/_lib/iconsMap.const'
import { IPreventiveExamination, IAnnualProgram, IMedanteCreditProgram, IBenefit } from '@/_types'

import { ZAPDetailBenefitBlocks } from './z-ap-detail-editorjs-renderer'

interface IProps {
	program: IPreventiveExamination | IAnnualProgram | IMedanteCreditProgram
}

interface ILayout {
	leftColumn: IBenefit[]
	rightColumn: IBenefit[]
	fullWidth: IBenefit[]
}

interface IBenefitItemProps {
	benefit: IBenefit
	className?: string
}

const BenefitItem = memo(({ benefit, className }: IBenefitItemProps) => {
	const iconSrc = useMemo(() => getIcon(benefit.icon), [benefit.icon])

	return (
		<li className={clsx('item', className)}>
			<h4>
				<strong>{benefit.name}</strong>
			</h4>
			<ZAPDetailBenefitBlocks data={benefit.description} id={benefit.id} />
			{iconSrc && <img src={iconSrc as string} alt={benefit.icon} />}
		</li>
	)
})

BenefitItem.displayName = 'BenefitItem'

const ZAPDetailContent = memo(({ program }: IProps) => {
	const t = useTranslations('pages.preventiveExaminationsCategories.overview')
	const [layout, setLayout] = useState<ILayout | null>(null)
	const measureRef = useRef<HTMLDivElement>(null)

	const sortedBenefits = useMemo(() => {
		return program.benefits.sort((a, b) => a.sortOrder - b.sortOrder)
	}, [program.benefits])

	// Measure rendered items and calculate desktop two-column layout in background
	useEffect(() => {
		if (!measureRef.current || layout !== null) return

		const measureElements = measureRef.current.querySelectorAll('[data-benefit-id]')
		const heights = new Map<number, number>()

		measureElements.forEach(element => {
			const id = parseInt(element.getAttribute('data-benefit-id') || '0')
			heights.set(id, element.clientHeight)
		})

		if (sortedBenefits.length <= 2) {
			setLayout({ leftColumn: [], rightColumn: [], fullWidth: sortedBenefits })
			return
		}

		const fullWidth = sortedBenefits.slice(-2)
		const toDistribute = sortedBenefits.slice(0, -2)
		const leftColumn: IBenefit[] = []
		const rightColumn: IBenefit[] = []
		let leftHeight = 0
		let rightHeight = 0

		toDistribute.forEach(benefit => {
			const height = heights.get(benefit.id) || 0
			if (leftHeight <= rightHeight) {
				leftColumn.push(benefit)
				leftHeight += height
			} else {
				rightColumn.push(benefit)
				rightHeight += height
			}
		})

		setLayout({ leftColumn, rightColumn, fullWidth })
	}, [sortedBenefits, layout])

	return (
		<Accordion
			type='single'
			collapsible
			defaultValue={program.isExpanded ? 'item-1' : undefined}
			suppressHydrationWarning={true}
		>
			<AccordionItem value='item-1'>
				<AccordionTrigger className='px-6 text-base font-semibold border-b-2 border-b-primary rounded-b-none'>
					{t('contains')}
				</AccordionTrigger>
				<AccordionContent suppressHydrationWarning>
					{/* Hidden measurement div — only needed until layout is calculated */}
					{!layout && (
						<div ref={measureRef} className='opacity-0 pointer-events-none absolute'>
							<ul className='item_complex-health_text'>
								{sortedBenefits.map((benefit: IBenefit, index: number) => (
									<div
										key={`${program.id}-measure-${index}`}
										data-benefit-id={benefit.id}
									>
										<BenefitItem benefit={benefit} />
									</div>
								))}
							</ul>
						</div>
					)}

					<div className='item_complex-health_text'>
						{/* Mobile + pre-layout: single column, always SSR-rendered */}
						<ul className={layout ? 'lg:hidden' : ''}>
							{sortedBenefits.map((benefit: IBenefit) => (
								<BenefitItem
									key={`${program.id}-mobile-${benefit.id}`}
									benefit={benefit}
								/>
							))}
						</ul>

						{/* Desktop: two balanced columns, rendered after measurement */}
						{layout && (
							<div className='hidden lg:block'>
								<div className='grid grid-cols-2 gap-16'>
									<ul>
										{layout.leftColumn.map((benefit: IBenefit) => (
											<BenefitItem
												key={`${program.id}-left-${benefit.id}`}
												benefit={benefit}
											/>
										))}
									</ul>
									<ul>
										{layout.rightColumn.map((benefit: IBenefit) => (
											<BenefitItem
												key={`${program.id}-right-${benefit.id}`}
												benefit={benefit}
											/>
										))}
									</ul>
								</div>

								{layout.fullWidth.length > 0 && (
									<ul>
										{layout.fullWidth.map((benefit: IBenefit) => (
											<BenefitItem
												key={`${program.id}-full-${benefit.id}`}
												benefit={benefit}
											/>
										))}
									</ul>
								)}
							</div>
						)}
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
})

ZAPDetailContent.displayName = 'ZAPDetailContent'

export default ZAPDetailContent
